/**
 * Canvas snap helpers for Review draw tools (Phase 3).
 * Snaps in canvas space; sources stored as PDF page points.
 */

import { pdfPointToCanvas } from './userMarkup.js';
import { applyOrthoLock, resolveOrthoAxis } from './markupGeometry.js';
import { arcPdfVertices, circlePdfVertices } from './arcGeometry.js';

function pushPdfPoint(bucket, px, py, kind, source = 'extract') {
  if (!Number.isFinite(px) || !Number.isFinite(py)) return;
  bucket.push({ pdf: [px, py], kind, source });
}

function pushSegmentMidpoints(bucket, pdfPoints, source) {
  for (let i = 0; i < pdfPoints.length - 1; i += 1) {
    const [x0, y0] = pdfPoints[i];
    const [x1, y1] = pdfPoints[i + 1];
    pushPdfPoint(bucket, (x0 + x1) / 2, (y0 + y1) / 2, 'midpoint', source);
  }
}

function pushBboxCorners(bucket, bbox, source) {
  if (!Array.isArray(bbox) || bbox.length < 4) return;
  const [x, y, w, h] = bbox;
  pushPdfPoint(bucket, x, y, 'endpoint', source);
  pushPdfPoint(bucket, x + w, y, 'endpoint', source);
  pushPdfPoint(bucket, x + w, y + h, 'endpoint', source);
  pushPdfPoint(bucket, x, y + h, 'endpoint', source);
  pushPdfPoint(bucket, x + w / 2, y + h / 2, 'center', source);
}

/**
 * @param {object[]} components — IR or user markup components on sheet
 * @param {number} pageIndex — zero-based page
 */
export function collectComponentSnapPoints(components, pageIndex) {
  const points = [];
  for (const comp of components || []) {
    if ((comp.pageIndex ?? 0) !== pageIndex) continue;
    const geom = comp.geometry;
    if (!geom) continue;
    const source = comp.source === 'user' ? 'user' : 'extract';

    if (geom.type === 'arc') {
      const verts = arcPdfVertices(geom);
      for (const pt of verts) {
        pushPdfPoint(points, pt[0], pt[1], 'endpoint', source);
      }
      if (verts.length >= 3) {
        pushPdfPoint(
          points,
          (verts[0][0] + verts[1][0]) / 2,
          (verts[0][1] + verts[1][1]) / 2,
          'midpoint',
          source,
        );
      }
    } else if (geom.type === 'circle') {
      const verts = circlePdfVertices(geom);
      for (const pt of verts) {
        pushPdfPoint(points, pt[0], pt[1], 'endpoint', source);
      }
      if (geom.center) {
        pushPdfPoint(points, geom.center[0], geom.center[1], 'center', source);
      }
    } else if (Array.isArray(geom.points) && geom.points.length) {
      for (const pt of geom.points) {
        if (Array.isArray(pt) && pt.length >= 2) {
          pushPdfPoint(points, pt[0], pt[1], 'endpoint', source);
        }
      }
      pushSegmentMidpoints(points, geom.points, source);
      if (geom.type === 'polygon' && geom.points.length >= 3) {
        const [a, b] = [geom.points[0], geom.points[geom.points.length - 1]];
        pushPdfPoint(points, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, 'midpoint', source);
      }
    }
    if (Array.isArray(geom.bbox) && geom.bbox.length >= 4) {
      pushBboxCorners(points, geom.bbox, source);
    }
  }
  return points;
}

/**
 * @param {object[]} entities — page entity shard entries
 */
export function collectEntitySnapPoints(entities) {
  const points = [];
  for (const ent of entities || []) {
    if (Array.isArray(ent.points) && ent.points.length) {
      for (const pt of ent.points) {
        if (Array.isArray(pt) && pt.length >= 2) {
          pushPdfPoint(points, pt[0], pt[1], 'endpoint', 'vector');
        }
      }
      pushSegmentMidpoints(points, ent.points, 'vector');
    }
    if (Array.isArray(ent.bbox) && ent.bbox.length >= 4) {
      pushBboxCorners(points, ent.bbox, 'vector');
    }
  }
  return points;
}

/**
 * Deduplicate snap targets that are very close in PDF space.
 */
export function dedupeSnapPoints(points, epsilon = 0.5) {
  const out = [];
  for (const p of points) {
    const dup = out.find((q) => (
      Math.hypot(q.pdf[0] - p.pdf[0], q.pdf[1] - p.pdf[1]) < epsilon
    ));
    if (!dup) out.push(p);
  }
  return out;
}

function pushSegment(bucket, a, b, source = 'extract') {
  if (!a || !b) return;
  bucket.push({ a: [a[0], a[1]], b: [b[0], b[1]], source });
}

function collectSegmentsFromPoints(points, source, closed = false) {
  const segs = [];
  if (!points || points.length < 2) return segs;
  for (let i = 0; i < points.length - 1; i += 1) {
    pushSegment(segs, points[i], points[i + 1], source);
  }
  if (closed && points.length >= 3) {
    pushSegment(segs, points[points.length - 1], points[0], source);
  }
  return segs;
}

export function collectSnapSegments(components, pageIndex, entities = []) {
  const segs = [];
  for (const comp of components || []) {
    if ((comp.pageIndex ?? 0) !== pageIndex) continue;
    const geom = comp.geometry;
    const source = comp.source === 'user' ? 'user' : 'extract';
    if (geom?.points?.length >= 2) {
      const closed = geom.type === 'polygon' || geom.type === 'bbox';
      segs.push(...collectSegmentsFromPoints(geom.points, source, closed));
    } else if (Array.isArray(comp.bbox) && comp.bbox.length >= 4) {
      const [x, y, w, h] = comp.bbox;
      const corners = [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
      segs.push(...collectSegmentsFromPoints(corners, source, true));
    }
  }
  for (const ent of entities || []) {
    if (ent.points?.length >= 2) {
      segs.push(...collectSegmentsFromPoints(ent.points, 'vector', false));
    }
  }
  return segs;
}

function cross(u, v) {
  return u[0] * v[1] - u[1] * v[0];
}

/** Segment-segment intersection in PDF space; returns [x,y] or null. */
export function segmentIntersection(segA, segB) {
  const a = segA.a;
  const b = segA.b;
  const c = segB.a;
  const d = segB.b;
  const r = [b[0] - a[0], b[1] - a[1]];
  const s = [d[0] - c[0], d[1] - c[1]];
  const denom = cross(r, s);
  if (Math.abs(denom) < 1e-9) return null;
  const qp = [c[0] - a[0], c[1] - a[1]];
  const t = cross(qp, s) / denom;
  const u = cross(qp, r) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return [a[0] + t * r[0], a[1] + t * r[1]];
}

/**
 * Intersection snap candidates (capped for performance on large sheets).
 */
export function collectIntersectionSnapPoints(segments, { maxTests = 8000, maxPoints = 120 } = {}) {
  const points = [];
  let tests = 0;
  outer:
  for (let i = 0; i < segments.length; i += 1) {
    for (let j = i + 1; j < segments.length; j += 1) {
      tests += 1;
      if (tests > maxTests) break outer;
      const hit = segmentIntersection(segments[i], segments[j]);
      if (hit) {
        points.push({ pdf: hit, kind: 'intersection', source: 'snap' });
        if (points.length >= maxPoints) break outer;
      }
    }
  }
  return dedupeSnapPoints(points, 1);
}

export function buildSnapPointIndex(components, pageIndex, entities = []) {
  const endpoints = [
    ...collectComponentSnapPoints(components, pageIndex),
    ...collectEntitySnapPoints(entities),
  ];
  const segments = collectSnapSegments(components, pageIndex, entities);
  const intersections = collectIntersectionSnapPoints(segments);
  return dedupeSnapPoints([...endpoints, ...intersections]);
}

/**
 * @param {{ x: number, y: number }} canvasPt
 * @param {object} viewport — pdf.js viewport
 * @param {object[]} snapPoints — from collectors above
 * @param {number} [thresholdPx]
 * @returns {{ canvas: {x,y}, pdf: [number,number], kind: string } | null}
 */
export function findSnapPoint(canvasPt, viewport, snapPoints, thresholdPx = 12) {
  if (!viewport || !canvasPt || !snapPoints?.length) return null;
  const candidates = [];
  for (const sp of snapPoints) {
    const c = pdfPointToCanvas(sp.pdf[0], sp.pdf[1], viewport);
    const d = Math.hypot(c.x - canvasPt.x, c.y - canvasPt.y);
    if (d <= thresholdPx) {
      candidates.push({ canvas: c, pdf: sp.pdf, kind: sp.kind, dist: d });
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.dist - b.dist);
  const best = candidates[0];
  return { canvas: best.canvas, pdf: best.pdf, kind: best.kind };
}

/** All snap hits within threshold — for TAB cycling (future). */
export function findSnapCandidates(canvasPt, viewport, snapPoints, thresholdPx = 12) {
  if (!viewport || !canvasPt || !snapPoints?.length) return [];
  return snapPoints
    .map((sp) => {
      const c = pdfPointToCanvas(sp.pdf[0], sp.pdf[1], viewport);
      const d = Math.hypot(c.x - canvasPt.x, c.y - canvasPt.y);
      return d <= thresholdPx ? { canvas: c, pdf: sp.pdf, kind: sp.kind, dist: d } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.dist - b.dist);
}

/**
 * Canvas points for ortho alignment — snap index plus optional extra vertices
 * (e.g. other handles on the markup being edited).
 */
export function collectOrthoAlignmentCanvasPoints(
  snapPoints,
  viewport,
  extraCanvasPoints = [],
  { excludeCanvasPoint = null, epsilonPx = 4 } = {},
) {
  if (!viewport) return [];
  const out = [];
  const seen = [];

  const push = (pt) => {
    if (!pt || !Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return;
    if (excludeCanvasPoint) {
      const ex = excludeCanvasPoint;
      if (Math.hypot(pt.x - ex.x, pt.y - ex.y) < epsilonPx) return;
    }
    if (seen.some((q) => Math.hypot(q.x - pt.x, q.y - pt.y) < epsilonPx)) return;
    seen.push(pt);
    out.push(pt);
  };

  for (const sp of snapPoints || []) {
    push(pdfPointToCanvas(sp.pdf[0], sp.pdf[1], viewport));
  }
  for (const pt of extraCanvasPoints || []) {
    push(pt);
  }
  return out;
}

/**
 * Ortho lock + alignment snap to other vertices (same X while horizontal,
 * same Y while vertical, and endpoints lying on the ortho ray).
 */
export function applyOrthoSnap(
  raw,
  origin,
  alignmentCanvasPoints,
  thresholdPx = 12,
  axis = null,
) {
  if (!origin || !raw) return { pt: raw, snap: null };

  const leg = axis || resolveOrthoAxis(origin, raw);
  const locked = applyOrthoLock(origin, raw, true, leg);
  const horizontal = leg === 'horizontal';
  const onAxisEps = 2;

  let bestPt = locked;
  let bestDist = thresholdPx;
  let snap = null;

  for (const p of alignmentCanvasPoints || []) {
    if (horizontal) {
      // Horizontal leg through origin: snap X to other vertices; grab on-axis endpoints.
      if (Math.abs(p.y - origin.y) <= onAxisEps) {
        const d = Math.abs(p.x - locked.x);
        if (d < bestDist) {
          bestDist = d;
          bestPt = { x: p.x, y: origin.y };
          snap = bestPt;
        }
      }
      const alignDist = Math.abs(p.x - locked.x);
      if (alignDist < bestDist) {
        bestDist = alignDist;
        bestPt = { x: p.x, y: origin.y };
        snap = bestPt;
      }
    } else {
      // Vertical leg through origin: snap Y to other vertices; grab on-axis endpoints.
      if (Math.abs(p.x - origin.x) <= onAxisEps) {
        const d = Math.abs(p.y - locked.y);
        if (d < bestDist) {
          bestDist = d;
          bestPt = { x: origin.x, y: p.y };
          snap = bestPt;
        }
      }
      const alignDist = Math.abs(p.y - locked.y);
      if (alignDist < bestDist) {
        bestDist = alignDist;
        bestPt = { x: origin.x, y: p.y };
        snap = bestPt;
      }
    }
  }

  return { pt: bestPt, snap };
}
